import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, Button, Section, Text, useToggle } from '@factorialco/deprecated-design-system'
import {
  type PermissionsGroupExternalsPageItemFragment,
  PermissionsGroupExternalsPageQueryDocument,
} from 'generated/resources/graphql'

import BoxList from 'modules/core/components/BoxList'
import Popover from 'modules/core/components/Popover'
import type { Option } from 'modules/core/components/Popover/Content'
import PopoverContent from 'modules/core/components/Popover/Content'
import { useQuery } from 'modules/core/hooks/useQuery'
import { i18n } from 'modules/core/testing/i18n'

import EditUserModal from 'modules/external_users/components/EditUserModal'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { PermissionGroupsLayout } from 'modules/permissions/components/PermissionGroupsLayout'
import AssignUsersModal from 'modules/permissions/components/Permissions/AssignUsersModal'
import useAddMembersToPermissionGroup from 'modules/permissions/hooks/useAddMembersToPermissionGroup'
import { PermissionGroupContainerProps } from 'modules/permissions/pages/settings/PermissionGroupContainer'

import Item from './Item'

const Externals = () => {
  const { permissionGroup } = useOutletContext<PermissionGroupContainerProps>()

  const { data, loading, refetch } = useQuery(PermissionsGroupExternalsPageQueryDocument, {
    variables: {
      permissionGroupId: permissionGroup.id,
    },
  })

  const members: PermissionsGroupExternalsPageItemFragment[] =
    data?.permissions.permissionGroup?.membersConnection.edges.map((edge) => edge.node) ?? []

  const description = (): string => {
    return i18n.t('settings.permissions.group.employees_section.external_group.description', {
      group: permissionGroup.attributes.name,
    })
  }

  const [open, toggle] = useToggle(false)
  const [assignUserModalOpen, setAssignUserModalOpen] = useState(false)
  const [isNewUserModalOpen, setIsNewUserModalOpen] = useState(false)
  const [addMembers] = useAddMembersToPermissionGroup()

  const options = (): Option[] => {
    const allowedOptions = []

    if (data?.permissions._policies.groupAssignEmployees.can ?? false) {
      allowedOptions.push({
        onClick: () => setAssignUserModalOpen(true),
        title: i18n.t('settings.permissions.group.actions.assign_users.title'),
        description: i18n.t('settings.permissions.group.actions.assign_users.description'),
      })
    }

    if (data?.authentication._policies.accessCreateExternal.can ?? false) {
      allowedOptions.push({
        onClick: () => setIsNewUserModalOpen(true),
        title: i18n.t('settings.permissions.group.actions.new_user.title'),
        description: i18n.t('settings.permissions.group.actions.new_user.description'),
      })
    }

    return allowedOptions
  }

  const renderList = () => {
    if (members.length === 0) {
      return (
        <Box
          background="grey000White"
          flexDirection="row"
          justifyContent="center"
          width="full"
          padding="s32"
        >
          <Text.Normal weight="medium">{i18n.t('settings.external_users.no_users')}</Text.Normal>
        </Box>
      )
    }

    return (
      <BoxList>
        {members.map((user) => (
          <Item
            key={user.id}
            user={user}
            canAssign={data?.permissions._policies.groupAssignEmployees.can ?? false}
            canEdit={user._policies?.update?.can ?? false}
            onUpdate={refetch}
          />
        ))}
      </BoxList>
    )
  }

  return (
    <ResponsivePage.Body loading={loading} error={null}>
      <PermissionGroupsLayout />
      <Section.FullWidth
        illustration="laptop-user"
        title={permissionGroup.title}
        description={description()}
      >
        {options().length && (
          <Box alignItems="flexEnd" paddingBottom="s24">
            <Popover
              attachment="top right"
              targetAttachment="bottom center"
              toggle={toggle}
              open={open}
            >
              <Button onClick={toggle(true)} iconName="add" />
              <PopoverContent onClose={toggle(false)} options={options()} />
            </Popover>
          </Box>
        )}
        {renderList()}
      </Section.FullWidth>
      {assignUserModalOpen && (
        <AssignUsersModal
          onClose={() => {
            setAssignUserModalOpen(false)
            refetch()
          }}
          permissionGroup={permissionGroup}
          external
          excludeIds={members.map((member) => member.id)}
        />
      )}
      {isNewUserModalOpen && (
        <EditUserModal
          onClose={() => {
            setIsNewUserModalOpen(false)
            refetch()
          }}
          onSuccess={(userId) => {
            addMembers({
              permissionGroupId: permissionGroup.id,
              accessIds: [userId],
            })
          }}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default Externals
